'use client';

import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { useCopy } from '@/app/hooks';
import type { LiveAvatar } from '@/app/types';
import { downloadElementAsImage, reportEvent } from '@/app/utils';
import { isUserBannerAvatar } from '../utils';
import { RoleShareCard } from './RoleShareCard';

interface ShareAvatarDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 分享落地页链接，形如 `${origin}/vidu-stream/share-avatar/${roleId}`。 */
  shareUrl: string;
  role: LiveAvatar;
}

export const ShareAvatarDialog = ({ open, onOpenChange, shareUrl, role }: ShareAvatarDialogProps) => {
  const t = useTranslations('vidu-stream.avatars');
  const { copy } = useCopy();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveImage = async () => {
    if (!cardRef.current) return;

    setIsSaving(true);
    try {
      await downloadElementAsImage(cardRef.current, {
        filename: `${role.name || 'role'}-share-card.png`,
        renderAttempts: 3,
      });

      toast({ type: 'success', message: t('share.saveImageSuccess') });

      reportEvent('stream_element_click', {
        element_name: 'save_image_click',
        stream_character_name: role.name ?? '',
        stream_character_id: role.id ?? '',
        stream_character_type: isUserBannerAvatar(role) ? 'ugc' : 'pgc',
      });
    } catch (error) {
      console.error('保存图片失败:', error);
      toast({ type: 'error', message: t('share.saveImageFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex w-[min(480px,calc(100%-32px))] max-w-none flex-col gap-0 border-system-white08 bg-[#1c1c1c] p-0 text-white"
        onClose={() => onOpenChange(false)}
      >
        <DialogHeader className="shrink-0 gap-0 space-y-0 px-6 pt-6 pb-3">
          <DialogTitle className="text-left font-medium text-white text-xl leading-tight">
            {t('share.title')}
          </DialogTitle>
        </DialogHeader>

        {/* 角色卡片 */}
        <div className="flex justify-center px-6 pb-6">
          <div ref={cardRef}>
            <RoleShareCard role={role} qrCodeUrl={shareUrl} />
          </div>
        </div>

        {/* 按钮组 */}
        <div className="flex shrink-0 gap-3 px-6 pb-6">
          <Button
            variant="outline"
            size="dialog"
            className="flex-1"
            onClick={() => {
              copy(shareUrl, { copySuccessMessage: t('menu.linkCopied') });
              reportEvent('stream_element_click', {
                element_name: 'copy_link_click',
                stream_character_name: role.name ?? '',
                stream_character_id: role.id ?? '',
                stream_character_type: isUserBannerAvatar(role) ? 'ugc' : 'pgc',
              });
            }}
          >
            {t('share.copyLink')}
          </Button>
          <Button variant="primary" size="dialog" className="flex-1" onClick={handleSaveImage} disabled={isSaving}>
            {isSaving ? t('share.saving') : t('share.saveImage')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
